// Full-document render of the tempurpedic MattressModulesPage: PageSkeleton
// (the React document shell) wrapping the three body sections in order
// (normal site chrome):
//
// 1. `.mattress-modules__hero-container` (always rendered, even when `hero` is
//    empty) around the MattressHeroModule blocks -- two markup variants keyed
//    on `hero_style` (`couple` dynamic pretitle/title vs the static `andy`
//    banner with its hardcoded tooltip title), any other value renders
//    nothing; no per-block Wagtail `w-block-*` wrapper is rendered here.
// 2. The page-bespoke "Exclusive Benefits" value-prop bar(s): one bar per
//    `value_prop_bar` block in `extra_copy` (the ONLY extra_copy block type
//    rendered here; other block types are silently dropped -- preserved
//    contract). The header strings arrive server-gettext-resolved (fr_CA);
//    ValueProp title/subtitle are raw rich-text source.
// 3. `.mattress-modules` around the plain-Toolbox `modules` stream, dispatched
//    through the shared page map with the 7 SSR side-channels, rendered via
//    `tagName={React.Fragment}` (no `w-block-*` wrappers).
import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.MattressModulesPageTemplate;

type IHeroBlock = NonNullable<IProps["page"]["hero"]>["value"][number];
type IHeroValue = Extract<IHeroBlock, { type: "hero" }>["value"];
type IValuePropBar = IProps["value_prop_bars"][number];

const MattressHero = (props: {
    value: Immutable<IHeroValue>;
}): React.JSX.Element | null => {
    const { value } = props;
    if (value.hero_style === "couple") {
        return (
            <div className="mattress-module-hero mattress-module-hero--couple l-full-width">
                <div className="mattress-module-hero__title-container--couple">
                    <div className="mattress-module-hero__pretitle mattress-module-hero__pretitle--couple">
                        {value.hero_pretitle}
                    </div>
                    <div className="mattress-module-hero__title mattress-module-hero__title--couple">
                        {value.hero_title}
                    </div>
                </div>
            </div>
        );
    }
    if (value.hero_style === "andy") {
        // The tooltip title is hardcoded, non-translated copy -- a
        // pre-existing i18n gap, preserved as-is.
        return (
            <div className="mattress-module-hero mattress-module-hero--andy l-full-width">
                <div className="l-capped-width">
                    <div
                        className="mattress-module-hero__title-container--andy"
                        title="Any Tempur-Pedic mattress plus Free Delivery, 90-Day Trial, and a 10 year Limited Warranty equals Life Changing Sleep"
                    ></div>
                </div>
            </div>
        );
    }
    return null;
};

const ValuePropBar = (props: {
    bar: Immutable<IValuePropBar>;
    headerTitle: string;
    headerDesc: string;
}): React.JSX.Element => {
    return (
        <div className="value-prop-bar value-prop-bar--mattress-modules">
            <div className="value-prop-bar__inner-container--mattress-modules l-capped-width">
                <div className="value-prop-bar__header value-prop-bar__header--mattress-modules">
                    <div className="value-prop-bar__triangle-container">
                        <div className="value-prop-bar__triangle"></div>
                    </div>
                    <div className="value-prop-bar__header-copy value-prop-bar__header-copy--mattress-modules">
                        <div className="value-prop-bar__header-title value-prop-bar__header-title--mattress-modules">
                            {props.headerTitle}
                        </div>
                        <div className="value-prop-bar__header-desc value-prop-bar__header-desc--mattress-modules">
                            {props.headerDesc}
                        </div>
                    </div>
                </div>
                <ul className="value-prop-list value-prop-list--mattress-modules">
                    {props.bar.value_props.map((prop, i) => (
                        <li
                            key={i}
                            className="value-prop-list-item value-prop-list-item--mattress-modules"
                        >
                            <div className="value-prop-list-item__icon value-prop-list-item__icon--mattress-modules">
                                <div
                                    className="value-prop-list-icon value-prop-list-icon--mattress-modules"
                                    style={{
                                        backgroundImage: `url(${prop.icon_url})`,
                                    }}
                                    role="img"
                                    aria-hidden="true"
                                ></div>
                            </div>
                            <div className="value-prop-list-item__copy">
                                {/* CMS RichText source. */}
                                <div
                                    className="value-prop-list-item__title value-prop-list-item__title--mattress-modules"
                                    dangerouslySetInnerHTML={{
                                        __html: prop.title_html,
                                    }}
                                />
                                <div
                                    className="value-prop-list-item__sub-title value-prop-list-item__sub-title--mattress-modules"
                                    dangerouslySetInnerHTML={{
                                        __html: prop.subtitle_html,
                                    }}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const hero = props.page.hero;
    const modules = props.page.modules;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="mattress-modules__hero-container">
                {hero?.value.map((block) => (
                    <MattressHero key={block.id} value={block.value} />
                ))}
            </div>
            {props.value_prop_bars.map((bar, i) => (
                <ValuePropBar
                    key={i}
                    bar={bar}
                    headerTitle={props.benefits_header_title}
                    headerDesc={props.benefits_header_desc}
                />
            ))}
            <div className="mattress-modules">
                {modules && (
                    <StreamField
                        tagName={React.Fragment}
                        value={modules.value}
                        components={buildToolboxPageMap(props)}
                    />
                )}
            </div>
        </PageSkeleton>
    );
};
